import React, { useMemo, useState } from "react";
import MarkedText from "./MarkedText";
import CheckWidget from "./CheckWidget";
import { compileExpr, sampleExpr } from "../lib/expr";
import { C } from "../lib/theme";
import { Eye, ArrowRight, RotateCcw, Check as CheckIcon, X as XIcon, PenLine } from "lucide-react";

// Renderers for the lesson block language (memory/LESSON_SPEC.md). The AI
// composes blocks; each type has exactly one Fathom-styled renderer here.
// Unknown types render nothing, so newer documents degrade instead of crash.

const Label = ({ color = C.blu, children }) => (
  <div className="mb-3 font-mono text-[11px] uppercase tracking-wider" style={{ color }}>{children}</div>
);

/* ---------- figures ---------- */

function FigureProcess({ steps, cycle }) {
  return (
    <div className="flex flex-wrap items-stretch gap-y-3">
      {steps.map((st, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-2 self-center text-muted/60" aria-hidden="true"><ArrowRight className="h-3.5 w-3.5" /></span>}
          <div className="min-w-0 rounded-md border px-3 py-2" style={{ borderColor: `${C.blu}55`, background: `${C.blu}0d` }}>
            <div className="font-mono text-[11px]" style={{ color: C.blu }}>{st.label}</div>
            {st.caption && <div className="mt-0.5 max-w-[180px] text-xs text-muted">{st.caption}</div>}
          </div>
        </React.Fragment>
      ))}
      {cycle && (
        <span className="ml-2 flex items-center gap-1 self-center font-mono text-[10px] uppercase tracking-wider text-muted">
          <RotateCcw className="h-3 w-3" /> repeats
        </span>
      )}
    </div>
  );
}

function FigureCompare({ left, right }) {
  const col = (side, color) => (
    <div className="rounded-md border border-hair p-3" style={{ borderTop: `2px solid ${color}` }}>
      <div className="mb-2 font-mono text-[11px] uppercase tracking-wider" style={{ color }}>{side.title}</div>
      <ul className="space-y-1.5">
        {side.points.map((p, i) => <li key={i} className="text-sm text-ink/90">· {p}</li>)}
      </ul>
    </div>
  );
  return <div className="grid gap-3 sm:grid-cols-2">{col(left, C.blu)}{col(right, C.amb)}</div>;
}

function FigureProportion({ items, caption }) {
  const max = Math.max(...items.map((i) => i.value), 1e-9);
  return (
    <div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-28 shrink-0 truncate text-right text-xs text-muted">{it.label}</span>
            <div className="h-4 flex-1 overflow-hidden rounded-sm bg-white/5">
              <div className="h-full rounded-sm" style={{ width: `${Math.max((it.value / max) * 100, 1.5)}%`, background: C.blu }} />
            </div>
            <span className="w-20 shrink-0 font-mono text-xs tab-nums" style={{ color: C.blu }}>{it.value}{it.unit}</span>
          </div>
        ))}
      </div>
      {caption && <p className="mt-2 text-xs text-muted">{caption}</p>}
    </div>
  );
}

function Figure({ block }) {
  return (
    <div className="rounded-lg border border-hair bg-surface2 p-4 hud-scan" style={{ borderLeft: "3px solid rgba(255,255,255,0.18)" }} data-testid={`figure-${block.kind}`}>
      <Label>{{ process: "The chain", cycle: "The cycle", compare: "Side by side", proportion: "The numbers" }[block.kind]}</Label>
      {(block.kind === "process" || block.kind === "cycle") && <FigureProcess steps={block.steps} cycle={block.kind === "cycle"} />}
      {block.kind === "compare" && <FigureCompare left={block.left} right={block.right} />}
      {block.kind === "proportion" && <FigureProportion items={block.items} caption={block.caption} />}
    </div>
  );
}

/* ---------- reveal / tryIt ---------- */

function Reveal({ block }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-hair bg-surface2 p-4" style={{ borderLeft: `3px solid ${C.vio}` }} data-testid="block-reveal">
      <Label color={C.vio}>Think first</Label>
      <p className="text-sm text-ink/90">{block.question}</p>
      {open ? (
        <p className="snap-in mt-3 rounded-md border px-3 py-2 text-sm text-ink/90" style={{ borderColor: `${C.vio}55`, background: `${C.vio}0d` }}>{block.answer}</p>
      ) : (
        <button
          data-testid="reveal-answer-btn"
          onClick={() => setOpen(true)}
          className="press mt-3 rounded-md border border-dashed border-hair px-3 py-1.5 text-sm text-muted hover:border-white/25 hover:text-ink"
        >
          <Eye className="mr-1.5 inline h-3.5 w-3.5" /> I have an answer — show me
        </button>
      )}
    </div>
  );
}

function TryIt({ block, onResult }) {
  const [text, setText] = useState("");
  const [compared, setCompared] = useState(false);
  const [marked, setMarked] = useState(null);
  return (
    <div className="rounded-lg border border-hair bg-surface2 p-4" style={{ borderLeft: `3px solid ${C.amb}` }} data-testid="block-tryit">
      <Label color={C.amb}><PenLine className="mr-1 inline h-3 w-3" /> Your turn</Label>
      <p className="text-sm text-ink/90">{block.prompt}</p>
      <textarea
        data-testid="tryit-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={compared}
        rows={2}
        placeholder="One or two sentences, in your own words…"
        className="mt-3 w-full resize-none rounded-md border border-hair bg-transparent px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-amb/60 focus:outline-none"
      />
      {!compared ? (
        <button
          data-testid="tryit-compare"
          onClick={() => setCompared(true)}
          disabled={!text.trim()}
          className="press mt-2 rounded-full border border-hair px-4 py-1.5 text-sm text-ink hover:bg-white/5 disabled:opacity-40"
        >
          Compare with the model answer
        </button>
      ) : (
        <div className="mt-3 anim-fade-up">
          <div className="rounded-md border border-hair bg-surface px-3 py-2 text-sm text-ink/90">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted">Model answer · </span>
            {block.modelAnswer}
          </div>
          {marked === null ? (
            <div className="mt-2 flex gap-2">
              <button data-testid="tryit-had-it" onClick={() => { setMarked(true); onResult && onResult(true); }}
                className="press flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs" style={{ borderColor: C.grn, color: C.grn }}>
                <CheckIcon className="h-3 w-3" /> I had it
              </button>
              <button data-testid="tryit-missed" onClick={() => { setMarked(false); onResult && onResult(false); }}
                className="press flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs" style={{ borderColor: C.ros, color: C.ros }}>
                <XIcon className="h-3 w-3" /> I missed something
              </button>
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted">{marked ? "Locked in." : "Noted — this feeds your focused review."}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- branch ---------- */

function Branch({ block, onResult }) {
  const [picked, setPicked] = useState(null);
  const done = picked !== null;
  const chosen = done ? block.options[picked] : null;
  return (
    <div className="rounded-lg border border-hair bg-surface2 p-4" style={{ borderLeft: `3px solid ${C.ros}` }} data-testid="block-branch">
      <Label color={C.ros}>Checkpoint</Label>
      <p className="text-sm text-ink/90">{block.question}</p>
      <div className="mt-3 space-y-2">
        {block.options.map((o, i) => {
          const show = done && (o.correct || picked === i);
          return (
            <button
              key={i}
              disabled={done}
              data-testid={`branch-opt-${i}`}
              onClick={() => { setPicked(i); onResult && onResult(!!block.options[i].correct); }}
              className="block w-full rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:cursor-default"
              style={{
                borderColor: show ? (o.correct ? C.grn : C.ros) : "rgba(255,255,255,0.08)",
                background: show ? `${o.correct ? C.grn : C.ros}14` : "transparent",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {done && (
        <div className="mt-3 anim-fade-up">
          {chosen.feedback && <p className="text-sm text-ink/80">{chosen.feedback}</p>}
          {!chosen.correct && chosen.blocks?.length > 0 && (
            <div className="mt-3 space-y-3 border-l-2 pl-3" style={{ borderColor: `${C.ros}66` }} data-testid="branch-remedial">
              <BlockList blocks={chosen.blocks} onResult={onResult} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- explore ---------- */

const W = 560, H = 190, PAD = 34;

function Explore({ block }) {
  const f = useMemo(() => compileExpr(block.expr), [block.expr]);
  const pts = useMemo(() => (f ? sampleExpr(f, block.xMin, block.xMax) : []), [f, block]);
  const [px, setPx] = useState(0.6);
  if (!f) return null; // backend drops these too; belt and braces

  const ys = pts.filter((p) => p.y !== null).map((p) => p.y);
  if (!ys.length) return null;
  let yMin = Math.min(...ys), yMax = Math.max(...ys);
  if (yMin === yMax) { yMin -= 1; yMax += 1; }
  const sx = (x) => PAD + ((x - block.xMin) / (block.xMax - block.xMin)) * (W - PAD * 2);
  const mapY = (y) => H - PAD - ((y - yMin) / (yMax - yMin)) * (H - PAD * 2);

  let d = "", pen = false;
  for (const p of pts) {
    if (p.y === null) { pen = false; continue; }
    d += `${pen ? "L" : "M"}${sx(p.x).toFixed(1)},${mapY(p.y).toFixed(1)}`;
    pen = true;
  }
  const cx = block.xMin + px * (block.xMax - block.xMin);
  const cy = f(cx);
  const fmt = (v) => (Math.abs(v) >= 1000 || (Math.abs(v) < 0.01 && v !== 0) ? v.toExponential(1) : +v.toFixed(2));

  const drag = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = ((e.clientX - rect.left) / rect.width) * W;
    setPx(Math.min(1, Math.max(0, (rel - PAD) / (W - PAD * 2))));
  };

  return (
    <div className="rounded-lg border border-hair bg-surface2 p-4 hud-scan" style={{ borderLeft: `3px solid ${C.lim}` }} data-testid="block-explore">
      <Label color={C.lim}>{block.title || "Explore it"}</Label>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full cursor-crosshair select-none"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); drag(e); }}
        onPointerMove={(e) => e.buttons && drag(e)}
        data-testid="explore-graph"
      >
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="rgba(255,255,255,0.15)" />
        <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="rgba(255,255,255,0.15)" />
        <path d={d} fill="none" stroke={C.lim} strokeWidth="2" />
        {Number.isFinite(cy) && (
          <>
            <line x1={sx(cx)} y1={PAD} x2={sx(cx)} y2={H - PAD} stroke={`${C.lim}55`} strokeDasharray="3 3" />
            <circle cx={sx(cx)} cy={mapY(Math.min(Math.max(cy, yMin), yMax))} r="5" fill={C.lim} />
          </>
        )}
        <text x={W - PAD} y={H - 10} textAnchor="end" fontSize="10" fill="#8B93A5" fontFamily="JetBrains Mono, monospace">{block.xLabel}</text>
        <text x={10} y={PAD - 8} fontSize="10" fill="#8B93A5" fontFamily="JetBrains Mono, monospace">{block.yLabel}</text>
      </svg>
      <div className="mt-1 flex items-center justify-between font-mono text-xs tab-nums">
        <span className="text-muted">{block.xLabel}: <span className="text-ink">{fmt(cx)}</span></span>
        <span className="text-muted">{block.yLabel}: <span style={{ color: C.lim }}>{Number.isFinite(cy) ? fmt(cy) : "—"}</span></span>
      </div>
      {block.caption && <p className="mt-2 text-xs text-muted">{block.caption}</p>}
      <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted/70">Drag across the graph</p>
    </div>
  );
}

/* ---------- registry ---------- */

export default function BlockList({ blocks, onResult }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.t) {
          case "prose": return <MarkedText key={i} text={b.md} className="text-ink/90" />;
          case "figure": return <Figure key={i} block={b} />;
          case "check": return <CheckWidget key={i} check={b.widget} onResult={onResult} />;
          case "reveal": return <Reveal key={i} block={b} />;
          case "tryIt": return <TryIt key={i} block={b} onResult={onResult} />;
          case "branch": return <Branch key={i} block={b} onResult={onResult} />;
          case "explore": return <Explore key={i} block={b} />;
          default: return null;
        }
      })}
    </>
  );
}
